import { useNavigate, useParams } from "react-router-dom";
import { IProductAdmin } from "../../models/admin/products";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { Swiper as SwiperType } from "swiper";
import "swiper/css";
import "swiper/css/navigation";
import { Navigation } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import api from "../../api/api";
import { environment } from "../../environments/environment";
import moment from "moment";
import { FaChevronLeft, FaChevronRight, FaTimes, FaCheckCircle, FaFacebookF } from "react-icons/fa";
import { Box, Skeleton, Stack, Tab, Tabs } from '@mui/material';
import { currencyFormat } from "../../utils/format-currency";
import { ItemCountInput } from "../../components/item-count-input";
import { FaXTwitter } from "react-icons/fa6";
import { IAppState } from "../../store/store";
import { toast } from "react-toastify";
  import Select from "react-select";

interface ProductImagesProp {
  uuid: string;
    sort_id: number;
    media_url: string;
}

export const ProductSingle = () => {
  const isLoggedIn = useSelector((state: IAppState) => state.auth.token);
  const [selectedSize, setSelectedSize] = useState<any>(null);
  const { id } = useParams();
  const navigate = useNavigate();
  const currencyView = "$";
      const dispatch = useDispatch();
      const [sizeError, setSizeError] = useState(false);
  const [productImages, setProductImages] = useState<ProductImagesProp[]>([]);
  const [productDetails, setProductDetails] = useState<IProductAdmin| null>(
    null
  );
  const [addedCartItem, setAddedCartItem] = useState<{
            image_url: string;
            name: string;
            quantity: number;
            size: string;
  } | null>(null);
  const [activeUuid, setActiveUuid] = useState<string | null>(null);
  const [itemCount, setItemCount] = useState(1);
  const [minQuantity, setMinQuantity] = useState(1);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [showCartPopup, setShowCartPopup] = useState(false);
  const [sizeKg, setSizeKg] = useState("");
  const [sizes, setSizes] = useState<any[]>([]);
  const swiperRef = useRef<SwiperType | null>(null);

  // Function to get the index of the image by its UUID
  const getIndexByUuid = (uuid: string) => {
    return productImages.findIndex((img) => img.uuid === uuid);
  };

  // Function to display the thumbnail the user clicked
  const handleThumbnailClick = (uuid: string) => {
    setActiveUuid(uuid);
    const index = getIndexByUuid(uuid);
    if (swiperRef.current && index !== -1) {
      swiperRef.current.slideToLoop(index);
    }
  };

  const formatSizeOptions = (product: IProductAdmin) => {
    const options: any[] = [];

    if (product.size_men_shoe) {
      options.push({
        label: "Men Shoe",
        options: product.size_men_shoe
          .split(",")
          .map((x: any) => ({ value: x, label: x })),
      });
    }

    if (product.size_women_shoe) {
      options.push({
        label: "Women Shoe",
        options: product.size_women_shoe
          .split(",")
          .map((x: any) => ({ value: x, label: x })),
      });
    }

    setSizes(options);
  };

  const fetchProductDetails = async () => {
    try {
      const response = await api.get(
        `${environment.baseUrl}/product/fetch/single/${id}`
      );
      const today = moment();
      let endsIn = 0;
      // Check if the deadline has passed
      const product = response.data.data?.product;

      if (today.isAfter(product.product_deadline)) {
        endsIn = 0;
      } else {
        // Calculate the number of days ahead
        endsIn = Math.abs(today.diff(product.product_deadline, "days"));
      }

      const apiResponseImages = response.data.data.product_images;
      const extractedImages = apiResponseImages.map((item: any) => {
        return {
          media_url: item.media_url,
          sort_id: item.sort_id,
          uuid: item.uuid,
        };
      });

      const sortedImages = extractedImages.sort(
        (a: ProductImagesProp, b: ProductImagesProp) => a.sort_id - b.sort_id
      );

      setProductImages(sortedImages);

      setProductDetails({ ...response.data.data?.product });
      formatSizeOptions({ ...response.data.data?.product });
      // setProductBacked(response.data.data?.product_backed ?? false)
      setMinQuantity(response.data.data?.product?.minimum_quantity ?? 1);
      setItemCount(response.data.data?.product?.minimum_quantity ?? 1);
      // dispatch(setSingleProduct(response.data.data?.product))
      setSizeKg(response.data.data?.product?.size_kg ?? "");
    } catch (error) {
      console.error("Error fetching product details:", error);
      navigate("/shop");
    }
  };

  const handleItemCountChange = (newValue: number) => {
    setItemCount(newValue);
  };

  const handleBuyYourself = async (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();

    if (!selectedSize) {
      setSizeError(true);
      toast.error("Please select a size");
      return;
    }

    const cart_uuid = localStorage.getItem("cart_uuid");

    try {
      setIsAddingToCart(true);

      if (!cart_uuid) {
        const createCartResponse = await api.post(
          `${environment.baseUrl}/cart/create`,
          {}
        );
        localStorage.setItem(
          "cart_uuid",
          createCartResponse.data.data.cart_uuid
        );
      }

      const response = await api.post(`${environment.baseUrl}/cart/item/add`, {
        cart_uuid: localStorage.getItem("cart_uuid"),
        product_uuid: id,
        quantity: itemCount,
        size_kg: selectedSize,
      });

      if (response.data.success) {
        setAddedCartItem({
          image_url:
            response.data.data.image_url ||
            (productImages.length > 0 ? productImages[0].media_url : ""),
          name: productDetails?.product_title || "",
          quantity: response.data.data.quantity || itemCount,
          size: response.data.data.size || selectedSize,
        });

        setShowCartPopup(true);

        setTimeout(() => {
          setShowCartPopup(false);
        }, 15000);
      } else {
        toast.error("Failed to add item to cart");
      }
    } catch (error) {
      console.error("Error adding item to cart:", error);
      toast.error("An error occurred while adding the item to the cart.");
    } finally {
      setIsAddingToCart(false);
    }
  };

  const handleShare = (platform: string) => {
    const url = window.location.href;
    const text = `Check out this product: ${productDetails?.product_title}`;
    let shareUrl = "";

    switch (platform) {
      case "facebook":
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
        break;
      case "twitter":
        shareUrl = `https://twitter.com/intent/tweet?url=${url}&text=${text}`;
        break;
      case "instagram":
        // Instagram does not support direct URL sharing
        alert("Instagram does not support direct URL sharing.");
        return;
      case "snapchat":
        // Snapchat does not support direct URL sharing
        alert("Snapchat does not support direct URL sharing.");
        return;
      case "tiktok":
        // TikTok does not support direct URL sharing
        alert("TikTok does not support direct URL sharing.");
        return;
      default:
        return;
    }

    window.open(shareUrl, "_blank");
  };

  useEffect(() => {
    fetchProductDetails();
  }, []);

  // the carousel for campaign images
  const repeatedRef = productDetails && (
    <div className="relative bg-white w-full h-full flex flex-col lg:flex-row items-start gap-3 justify-between">
      <div className="w-1/5 pr-2 h-[675px] hidden lg:block py-1.5 overflow-y-hidden overflow-x-hidden space-y-3">
        {productImages.map((img, index) => {
          const cloudinaryImageUrl = img.media_url.includes("cloudinary.com")
            ? img.media_url.replace(
                "/upload/",
                "/upload/w_250,h_250,c_fit,ar_1:1,f_auto,q_auto:best,dpr_auto,c_lpad/"
              )
            : img.media_url;

          return (
            <div
              key={img.uuid}
              className={`border h-12 w-12 2xl:h-14 2xl:w-14 rounded-lg mx-auto  cursor-pointer ${
                img.uuid === activeUuid ? "ring-1 ring-zinc-700" : ""
              }`}
            >
              <img
                src={cloudinaryImageUrl}
                className="object-cover w-full h-full rounded-xl"
                alt="Campaign image thumbnail"
                onClick={() => handleThumbnailClick(img.uuid)}
              />
            </div>
          );
        })}
      </div>
      <div className="w-full lg:w-4/5 ">
        <Swiper
          modules={[Navigation]}
          loop={true}
          spaceBetween={5}
          className="single-campaign-carousel"
          navigation={{
            nextEl: ".swiper-button-next",
            prevEl: ".swiper-button-prev",
          }}
          onSlideChange={(swiper: SwiperType) => {
            const realIndex = swiper.realIndex; // Get the real index (ignores the loop duplicates)
            setActiveUuid(productImages[realIndex]?.uuid || null);
          }}
          onSwiper={(swiper: SwiperType) => {
            swiperRef.current = swiper;
          }}
        >
          {productDetails &&
            productImages.map((img) => {
              const cloudinaryImageUrl = img.media_url.includes(
                "cloudinary.com"
              )
                ? img.media_url.replace(
                    "/upload/",
                    "/upload/w_1024,h_1536,c_fit,ar_1:1,f_auto,q_auto:best,dpr_auto,c_lpad/"
                  )
                : img.media_url;

              return (
                <SwiperSlide key={img.uuid} className="!h-[675px]">
                  <img
                    src={cloudinaryImageUrl}
                    className="object-cover w-full h-full"
                    alt="Campaign image"
                  />
                </SwiperSlide>
              );
            })}
          <button className="swiper-button-next">
            <FaChevronRight size={10} />
          </button>
          <button className="swiper-button-prev">
            <FaChevronLeft size={10} />
          </button>
        </Swiper>
      </div>
    </div>
  );

  // Cart popup component
  const CartPopup = () => {
    if (!showCartPopup || !addedCartItem) return null;

    // Get optimized image URL for the popup
    const imageUrl = addedCartItem.image_url.includes("cloudinary.com")
      ? addedCartItem.image_url.replace(
          "/upload/",
          "/upload/w_100,h_100,c_fit,ar_1:1,f_auto,q_auto:best,dpr_auto,c_lpad/"
        )
      : addedCartItem.image_url;

    return (
      <div className="fixed top-5 right-5 z-50 bg-white shadow-lg rounded-lg p-4 w-80 cart-popup-animation">
        <div className="flex items-center justify-between mb-2 pb-2 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <FaCheckCircle className="text-green-600 " />
            <span className="font-medium text-green-600">Added to cart</span>
          </div>
          {/* <span className="text-xs text-gray-500">Just now</span> */}
        </div>
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-16 h-16 rounded-md overflow-hidden flex-shrink-0">
              <img
                src={imageUrl}
                alt="Product"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex-1">
              <h4 className="font-medium text-sm">{addedCartItem.name}</h4>
              <p className="text-xs text-gray-600 mt-1">
                Quantity: {addedCartItem.quantity}
              </p>
              <p className="text-xs text-gray-600">
                Size: {addedCartItem.size}
              </p>
            </div>
          </div>
          <button
            className="text-gray-400 hover:text-gray-600"
            onClick={() => setShowCartPopup(false)}
          >
            <FaTimes />
          </button>
        </div>
        <div className="flex flex-col gap-2 mt-3">
          <button
            className="flex-1 py-2 px-3 border border-gray-300 hover:bg-gray-100 text-sm rounded-full font-medium transition-colors"
            onClick={() => navigate("/cart")}
          >
            View Cart
          </button>
          <button
            className="flex-1 py-2 px-3 bg-[#0E203F] text-white hover:bg-gray-800 text-sm rounded-full font-medium transition-colors"
            onClick={() => navigate("/checkout")}
          >
            Checkout
          </button>
        </div>
      </div>
    );
  };

  return productDetails ? (
    <div className="lg:py-8 py-4 web_padding_x grid grid-cols-1 lg:grid-cols-2 gap-10 xl:gap-14 2xl:gap-20">
      <CartPopup />

      <div className="lg:order-1 space-y-6">
        <h2 className="pb-8 web_heading_2">{productDetails?.product_title}</h2>

        <div className="lg:hidden">{repeatedRef}</div>

        {/* grid-cols-[auto_1fr] */}
        <form className="mt-10 flex flex-col tall:mt-14 2xl:mt-14   gap-4 sm:gap-6 lg:gap-7 ">
          <div className="flex flex-col gap-2">
            {sizeError && (
              <div className="text-red-500 text-sm">
                Please select a size before adding to cart
              </div>
            )}

            {/* Label and Select on the same row */}
            <div className="flex items-center gap-9">
              <div>
                <div className="sm:text-lg font-bold">Size</div>
                <div className="text-xs sm:text-sm">(Size Chart)</div>
              </div>

              <Select
                className="flex-1"
                classNamePrefix="custom_select"
                options={sizes}
                onChange={(e) => {
                  setSelectedSize(e?.value);
                  setSizeError(false);
                }}
                placeholder="Select size"
                styles={{
                  control: (base) => ({
                    ...base,
                    borderColor: sizeError ? "#ef4444" : base.borderColor,
                    "&:hover": {
                      borderColor: sizeError ? "#ef4444" : base.borderColor,
                    },
                  }),
                }}
              />
            </div>
          </div>

          <div className="sm:text-lg font-bold flex items-center gap-5">
            <p>Quantity</p>
            <div className="w-[120px]">
              <ItemCountInput
                value={itemCount}
                onValueChange={handleItemCountChange}
                min={minQuantity}
              />
            </div>
            <span className="font-normal text-sm">
              Minimum Quantity: {minQuantity}
            </span>
          </div>

          <div className="col-span-2 space-y-4">
            <button
              type="button"
              onClick={(event) => {
                // Capture the event parameter here
                handleBuyYourself(event);

                // Rest of your code
                // sessionStorage.setItem("campaignToBack", id!);
                sessionStorage.setItem("quantity", itemCount.toString());
                sessionStorage.setItem("productId", productDetails.uuid);
                // sessionStorage.setItem("size", selectedSize.toString());
                sessionStorage.setItem(
                  "yourself_price",
                  productDetails!.yourself_price.toString()
                );
              }}
              className="px-6 py-2 rounded-[24px] block w-[100%]   mx-auto text-white bg-[#0E203F] hover:bg-[#0E203F]/80"
            >
              Buy Yourself ({currencyView}{" "}
              {currencyFormat(productDetails?.yourself_price)})
            </button>
            <button
              type="button"
              onClick={() => {
                if (isLoggedIn) {
                  navigate(
                    `/friends-checkout/${productDetails.uuid}/${itemCount}`
                  );
                } else {
                  sessionStorage.setItem("itemCount", itemCount.toString());
                  sessionStorage.setItem("productId", productDetails.uuid);
                  navigate("/login");
                }

                // sessionStorage.setItem("campaignToBack", id!);
                // sessionStorage.setItem("quantity", itemCount.toString());
                // sessionStorage.setItem("size", selectedSize.toString());
                sessionStorage.setItem(
                  "friends_price",
                  productDetails!.friends_price.toString()
                );
              }}
              className="px-6 py-2 rounded-[24px] block w-[100%]   mx-auto text-white bg-[#3A86FF] hover:bg-[#3A86FF]/80"
            >
              Buy With Friends ({currencyView}{" "}
              {currencyFormat(productDetails?.friends_price)})
            </button>
            {/* 
            {mailchimpPopupVisible && (
              <MailchimpCampaginPopup
                onClose={() => setMailchimpPopupVisible(false)}
              />
            )} */}
            <div className="mt-4 text-center">
              {productDetails?.product_description}
            </div>

            <div className="flex justify-between items-center  mt-4 tall:mt-8 2xl:mt-8">
              <div className="flex gap-2 mx-auto items-center">
                <div
                  className="w-[34px] h-[34px] bg-[#0E203F] rounded-full grid place-content-center cursor-pointer"
                  onClick={() => handleShare("facebook")}
                >
                  <FaFacebookF className="text-white text-base" />
                </div>

                <div
                  className="w-[34px] h-[34px] bg-[#0E203F] rounded-full grid place-content-center cursor-pointer"
                  onClick={() => handleShare("twitter")}
                >
                  <FaXTwitter className="text-white text-base" />
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>

      <div>
        <div className="hidden lg:block h-screen max-h-[730px] w-full max-w-[600px] mx-auto">
          {repeatedRef}
        </div>
      </div>
    </div>
  ) : (
    <div className="web_padding_y web_padding_x">
      <Stack spacing={1}>
        <Stack spacing={1} direction={"row"}>
          <Skeleton variant="rounded" width={400} height={200} />
          <Skeleton variant="rounded" width={400} height={200} />
          <Skeleton variant="rounded" width={400} height={200} />
        </Stack>
        <Stack spacing={1} direction={"row"}>
          <Skeleton variant="rounded" width={400} height={200} />
          <Skeleton variant="rounded" width={400} height={200} />
          <Skeleton variant="rounded" width={400} height={200} />
        </Stack>
      </Stack>
    </div>
  );

}